import { useState, useCallback } from 'react';
import api from '../lib/api';

/**
 * Hook for handling audio transcription using OpenAI Whisper API
 *
 * @returns {Object} Hook state and functions
 */
export function useAudioTranscription() {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [error, setError] = useState(null);

  /**
   * Convert a Blob to Base64 string
   * @param {Blob} blob - The audio blob
   * @returns {Promise<string>} Base64 encoded string
   */
  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  /**
   * Get file extension from blob
   * @param {Blob} blob - The audio blob
   * @returns {string} File extension
   */
  const getFileExtension = useCallback((blob) => {
    let extension = 'webm';

    if (blob.type) {
      const typeParts = blob.type.split('/');
      if (typeParts[1]) {
        extension = typeParts[1].split(';')[0];
      }
    }

    // Handle file uploads with name property
    if (blob.name) {
      const nameParts = blob.name.split('.');
      if (nameParts.length > 1) {
        extension = nameParts[nameParts.length - 1].toLowerCase();
      }
    }

    return extension;
  }, []);

  /**
   * Transcribe audio blob using OpenAI Whisper
   * @param {Blob} audioBlob - The audio blob to transcribe
   * @returns {Promise<string|null>} Transcribed text or null on error
   */
  const transcribe = useCallback(async (audioBlob) => {
    if (!audioBlob) {
      setError('No hay audio para transcribir');
      return null;
    }

    setIsTranscribing(true);
    setError(null);

    try {
      const base64Audio = await blobToBase64(audioBlob);
      const fileExtension = getFileExtension(audioBlob);

      const response = await api.post('/ai/transcribe-audio', {
        audio_base64: base64Audio,
        file_extension: fileExtension
      });

      const text = response.data.transcription;
      setTranscription(text);
      return text;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Error al transcribir audio';
      setError(errorMessage);
      console.error('Transcription error:', err);
      return null;
    } finally {
      setIsTranscribing(false);
    }
  }, [blobToBase64, getFileExtension]);

  /**
   * Clear transcription state
   */
  const clearTranscription = useCallback(() => {
    setTranscription('');
    setError(null);
  }, []);

  return {
    isTranscribing,
    transcription,
    error,
    transcribe,
    clearTranscription,
    setTranscription
  };
}

export default useAudioTranscription;
